import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ESCUDO } from "@/lib/api";
import { KeyRound, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, ok: false, email: "", err: "" });
  const [form, setForm] = useState({ pwd: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setState({ checking: false, ok: true, email: r.data.email, err: "" }))
      .catch((e) => setState({ checking: false, ok: false, email: "", err: e.response?.data?.detail || "Enlace inválido" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.pwd.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    if (form.pwd !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.pwd });
      setDone(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo restablecer");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center p-6" data-testid="reset-page">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border p-8">
        <div className="flex items-center gap-3 mb-6">
          <img src={ESCUDO} alt="" className="w-12 h-12 rounded-full bg-white p-1 border-2 border-primary" />
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold">Recuperación</p>
            <h1 className="font-display text-2xl text-primary">Restablecer contraseña</h1>
          </div>
        </div>

        {state.checking && <p className="text-sm text-foreground/60">Verificando enlace…</p>}

        {!state.checking && !state.ok && (
          <div data-testid="reset-invalid">
            <p className="text-sm text-destructive font-semibold">{state.err}</p>
            <Link to="/login" className="block mt-5 text-sm text-primary hover:underline">← Volver al ingreso</Link>
          </div>
        )}

        {!state.checking && state.ok && !done && (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <p className="text-sm text-foreground/70">
              Define una nueva contraseña para <strong>{state.email}</strong>.
            </p>
            <div>
              <Label className="text-xs tracking-wider uppercase font-semibold">Nueva contraseña</Label>
              <div className="relative mt-1.5">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input type="password" required minLength={6} value={form.pwd} onChange={(e) => setForm({ ...form, pwd: e.target.value })} className="pl-9 h-11" data-testid="rp-pwd" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-wider uppercase font-semibold">Confirmar contraseña</Label>
              <div className="relative mt-1.5">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/50" />
                <Input type="password" required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="pl-9 h-11" data-testid="rp-confirm" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-full" data-testid="rp-submit">
              {busy ? "Guardando…" : "Restablecer contraseña"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        )}

        {done && (
          <div className="text-center py-4" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <p className="font-display text-xl text-primary mt-3">¡Listo!</p>
            <p className="text-sm text-foreground/70 mt-1">Te llevamos al ingreso…</p>
          </div>
        )}
      </div>
    </div>
  );
}
